import React, { createContext, useContext, useState, useEffect } from 'react';
import { authErrorMessage } from '../lib/authErrors';
import { authFetch } from '../lib/authFetch';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Auth tokens live in httpOnly cookies (security fix, Jul 2026) — never in localStorage.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [questionsRemaining, setQuestionsRemaining] = useState(3);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      // Purge any legacy token from the localStorage era
      try { localStorage.removeItem('access_token'); } catch { /* cookie auth does not require local storage */ }
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setQuestionsRemaining(userData.questions_remaining ?? 0);
        } else {
          await startAnonymousSession();
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        await startAnonymousSession();
      }
      setLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startAnonymousSession = async () => {
    let existingSessionId;
    try { existingSessionId = localStorage.getItem('session_id'); } catch { /* session-only browser */ }

    if (existingSessionId) {
      try {
        const response = await fetch(`${API_URL}/api/auth/session/status/${existingSessionId}`);
        if (response.ok) {
          const data = await response.json();
          setSessionId(existingSessionId);
          setQuestionsRemaining(data.questions_remaining);
          return;
        }
      } catch (error) {
        console.error('Session check failed:', error);
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        try { localStorage.setItem('session_id', data.session_id); } catch { /* session-only browser */ }
        setSessionId(data.session_id);
        setQuestionsRemaining(3);
      }
    } catch (error) {
      console.error('Failed to start session:', error);
      setQuestionsRemaining(0);
    }
  };

  const useQuestion = async () => {
    if (user) {
      setQuestionsRemaining(prev => Math.max(0, prev - 1));
      return true;
    } else if (sessionId) {
      try {
        const response = await fetch(`${API_URL}/api/auth/session/use-question/${sessionId}`, {
          method: 'POST'
        });

        if (response.ok) {
          const data = await response.json();
          setQuestionsRemaining(data.questions_remaining);
          return true;
        } else if (response.status === 403) {
          setQuestionsRemaining(0);
          return false;
        }
      } catch (error) {
        console.error('Failed to use question:', error);
        return false;
      }
    }
    return false;
  };

  const register = async (email, password, name) => {
    try {
      const response = await authFetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name })
      });

      if (response.ok) {
        const data = await response.json();
        try { localStorage.removeItem('session_id'); } catch { /* session-only browser */ }
        setUser(data.user);
        setSessionId(null);
        setAuthError('');
        setQuestionsRemaining(data.user.questions_remaining ?? 0);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: authErrorMessage(error.detail) };
      }
    } catch {
      return { success: false, error: 'Registration failed' };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        try { localStorage.removeItem('session_id'); } catch { /* session-only browser */ }
        setUser(data.user);
        setSessionId(null);
        setAuthError('');
        setQuestionsRemaining(data.user.questions_remaining ?? 0);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: authErrorMessage(error.detail) };
      }
    } catch {
      return { success: false, error: 'Login failed' };
    }
  };

  const logout = async () => {
    setAuthError('');
    try {
      const response = await authFetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Logout failed');
    } catch (error) {
      console.error('Logout call failed:', error);
      setAuthError('Sign-out could not be completed. Please try again.');
      return false;
    }
    setUser(null);
    await startAnonymousSession();
    return true;
  };

  const refreshUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        try { localStorage.removeItem('session_id'); } catch { /* session-only browser */ }
        setUser(userData);
        setSessionId(null);
        setAuthError('');
        setQuestionsRemaining(userData.questions_remaining ?? 0);
        return userData;
      }
    } catch (error) {
      console.error('User refresh failed:', error);
    }
    return null;
  };

  const value = {
    user,
    sessionId,
    questionsRemaining,
    isAuthenticated: !!user,
    loading,
    authError,
    useQuestion,
    register,
    login,
    logout,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
